import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';

/**
 * Admin only. Sets the bank's account category (SMA 0, SMA 1, SMA 2, NPA) on
 * all accounts of a bank (optionally one zone) without re-uploading a sheet.
 * Changes the category and nothing else.
 */
const CATEGORY_CHOICES = ['SMA 0', 'SMA 1', 'SMA 2', 'NPA'];
const CLEAR_VALUE = '__CLEAR__';

export const AccountCategoryModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { accounts, setAccountCategoryBulk } = useSRMS();
  const [bank, setBank] = useState<string>('');
  const [zone, setZone] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [confirmed, setConfirmed] = useState(false);

  const banks = useMemo(
    () => Array.from(new Set(accounts.map((a) => (a.bank || '').trim()).filter(Boolean))).sort(),
    [accounts]
  );

  const zones = useMemo(
    () =>
      Array.from(
        new Set(
          accounts
            .filter((a) => !bank || (a.bank || '').trim() === bank)
            .map((a) => (a.zone || '').trim())
            .filter(Boolean)
        )
      ).sort(),
    [accounts, bank]
  );

  const matches = useMemo(
    () =>
      accounts.filter(
        (a) =>
          (!bank || (a.bank || '').trim() === bank) &&
          (!zone || (a.zone || '').trim() === zone)
      ),
    [accounts, bank, zone]
  );

  const value = category === CLEAR_VALUE ? '' : category;
  const willChange = useMemo(
    () => (category ? matches.filter((a) => (a.accountCategory || '') !== value).length : 0),
    [matches, category, value]
  );

  const canApply = !!category && willChange > 0 && confirmed;

  const apply = () => {
    if (!canApply) return;
    const ids = matches.map((a) => a.accountId || a.id);
    const changed = setAccountCategoryBulk(ids, value);
    window.alert(`Account category set on ${changed} account(s).`);
    onClose();
  };

  const selectCls =
    'w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none';

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-5 space-y-4 text-xs">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-sm text-slate-900">Set account category</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" title="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-slate-600">
          Sets the category on existing accounts without re-uploading a sheet. Only the category changes.
          Remarks, visits, call logs, collections and agent assignments stay as they are.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-slate-700 font-bold mb-1">Bank</label>
            <select value={bank} onChange={(e) => { setBank(e.target.value); setZone(''); setConfirmed(false); }} className={selectCls}>
              <option value="">All banks</option>
              {banks.map((b) => (
                <option key={`cb-${b}`} value={b}>{b}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-slate-700 font-bold mb-1">Zone</label>
            <select value={zone} onChange={(e) => { setZone(e.target.value); setConfirmed(false); }} className={selectCls}>
              <option value="">All zones</option>
              {zones.map((z) => (
                <option key={`cz-${z}`} value={z}>{z}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-slate-700 font-bold mb-1">Category</label>
            <select value={category} onChange={(e) => { setCategory(e.target.value); setConfirmed(false); }} className={selectCls}>
              <option value="">Choose…</option>
              {CATEGORY_CHOICES.map((c) => (
                <option key={`cc-${c}`} value={c}>{c}</option>
              ))}
              <option value={CLEAR_VALUE}>Clear (blank)</option>
            </select>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-slate-700">
          <p>Accounts in this selection: <strong>{matches.length}</strong></p>
          <p>Accounts that will change: <strong>{category ? willChange : '—'}</strong></p>
        </div>

        <label className="flex items-start gap-2 text-slate-700">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            disabled={!category || willChange === 0}
            className="mt-0.5"
          />
          <span>I understand this changes the category on {category ? willChange : 0} account(s).</span>
        </label>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold border border-slate-200">
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={!canApply}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-xl font-bold"
          >
            Set category on {category ? willChange : 0} account(s)
          </button>
        </div>
      </div>
    </div>
  );
};
